import { Badge, Text, Tooltip, Group, Progress } from '@mantine/core';
import type {
  DataLibraryCoachPostSessionRow,
  DataLibrarySurveyQuestion,
  CoachPostSessionAttendee,
  CoachPostSessionFlag,
} from '../../../types';
import { SurveyResponsePill } from '../components/SurveyResponsePill';
import { GroupBadge } from '../../common';
import { ATTENDANCE_PILL_CONFIG } from '../sessions/SessionsCellRenderers';

function AttendancePill({ attendee }: { attendee: CoachPostSessionAttendee }) {
  const status = attendee.status;
  if (!status) {
    return (
      <Tooltip label={`${attendee.participant_name} — Not Marked`} withArrow>
        <Badge size="sm" color="gray" variant="light" styles={{ label: { fontSize: 11, textTransform: 'none' } }}>
          Not Marked
        </Badge>
      </Tooltip>
    );
  }
  const cfg = ATTENDANCE_PILL_CONFIG[status];
  if (!cfg) return null;
  return (
    <Tooltip label={`${attendee.participant_name} — ${cfg.label}`} withArrow>
      <Badge
        size="sm"
        styles={{
          root: { backgroundColor: cfg.bg },
          label: { color: cfg.text, fontSize: 11, textTransform: 'none' },
        }}
      >
        {cfg.label}
      </Badge>
    </Tooltip>
  );
}

const ENGAGEMENT_COLORS: Record<number, { bg: string; text: string; label: string }> = {
  5: { bg: '#28463A', text: '#fff', label: '5 — Highly Engaged' },
  4: { bg: '#4A7C59', text: '#fff', label: '4 — Engaged' },
  3: { bg: '#E9A23B', text: '#fff', label: '3 — Moderate' },
  2: { bg: '#E07B4C', text: '#fff', label: '2 — Low' },
  1: { bg: '#D64545', text: '#fff', label: '1 — Disengaged' },
};

function EngagementPill({ entry }: { entry: { participant_name: string; rating: number } }) {
  const cfg = ENGAGEMENT_COLORS[entry.rating] || { bg: '#888', text: '#fff', label: String(entry.rating) };
  return (
    <Tooltip label={`${entry.participant_name} — ${cfg.label}`} withArrow>
      <Badge
        size="sm"
        styles={{
          root: { backgroundColor: cfg.bg },
          label: { color: cfg.text, fontSize: 11, textTransform: 'none' },
        }}
      >
        {cfg.label}
      </Badge>
    </Tooltip>
  );
}

const CONCERN_TYPE_LABELS: Record<string, string> = {
  low_engagement: 'Low Engagement',
  attendance_issues: 'Attendance Issues',
  disruptive_behavior: 'Disruptive Behavior',
  personal_crisis: 'Personal Crisis',
  conflict_with_group_member: 'Conflict',
  technical_difficulties: 'Technical Difficulties',
  struggling_with_content: 'Struggling with Content',
  not_completing_prework: 'Not Completing Prework',
  flight_risk: 'Flight Risk',
  other: 'Other',
};

function ConcernFlagBadge({ flag }: { flag: CoachPostSessionFlag }) {
  const label = CONCERN_TYPE_LABELS[flag.concern_type] || flag.concern_type;
  const tooltip = `${flag.participant_name} — ${label}${flag.notes ? `: ${flag.notes}` : ''}`;
  return (
    <Tooltip label={tooltip} withArrow multiline w={260}>
      <Badge size="sm" color="red" variant="light" styles={{ label: { fontSize: 11, textTransform: 'none' } }}>
        {flag.participant_name}
      </Badge>
    </Tooltip>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() || '';
}

export function renderCoachPostSessionCell(
  row: DataLibraryCoachPostSessionRow,
  columnId: string,
  questions: DataLibrarySurveyQuestion[],
): React.ReactNode {
  switch (columnId) {
    case 'cohort_name':
      return <Text size="sm">{row.cohort_name}</Text>;
    case 'coach_name':
      return <Text size="sm">{row.coach_name || '—'}</Text>;
    case 'group_name':
      return <GroupBadge groupName={row.group_name} groupId={row.group_id} />;
    case 'session_number':
      return <Text size="sm">{row.session_number ?? '—'}</Text>;
    case 'session_topic':
      return <Text size="sm">{row.session_topic || '—'}</Text>;
    case 'session_date':
      return (
        <Text size="sm" c="dimmed">
          {row.session_date ? formatDate(row.session_date) : '—'}
        </Text>
      );
    case 'attendance':
      if (!row.attendance || row.attendance.length === 0) {
        return <Text size="xs" c="dimmed" fs="italic">No participants</Text>;
      }
      return (
        <Group gap={4} wrap="wrap">
          {row.attendance.map((a, i) => (
            <AttendancePill key={i} attendee={a} />
          ))}
        </Group>
      );
    case 'attendance_rate':
      if (row.attendance_rate === null) {
        return <Text size="sm" c="dimmed">&mdash;</Text>;
      }
      {
        const pct = row.attendance_rate;
        const color = pct >= 80 ? 'medleyGreen' : pct >= 60 ? 'orange' : 'dangerRed';
        return (
          <Tooltip label={`${pct}% attendance`} withArrow>
            <Group gap={6} wrap="nowrap">
              <Progress value={pct} color={color} size="sm" w={50} />
              <Text size="xs" c="dimmed">{pct}%</Text>
            </Group>
          </Tooltip>
        );
      }
    case 'absence_reasons':
      if (!row.absence_reasons) return <Text size="sm" c="dimmed">—</Text>;
      return (
        <Tooltip label={row.absence_reasons} withArrow multiline w={300}>
          <Text size="sm" lineClamp={2}>{row.absence_reasons}</Text>
        </Tooltip>
      );
    case 'coach_notes':
      if (!row.coach_notes) return <Text size="sm" c="dimmed">—</Text>;
      {
        const plain = stripHtml(row.coach_notes);
        return (
          <Tooltip label={plain} withArrow multiline w={300}>
            <Text size="sm" lineClamp={2}>{plain}</Text>
          </Tooltip>
        );
      }
    case 'engagement':
      if (!row.engagement || row.engagement.length === 0) {
        return <Text size="xs" c="dimmed" fs="italic">No ratings</Text>;
      }
      return (
        <Group gap={4} wrap="wrap">
          {row.engagement.map((e, i) => (
            <EngagementPill key={i} entry={e} />
          ))}
        </Group>
      );
    case 'avg_engagement':
      return (
        <Text size="sm">
          {row.avg_engagement !== null ? Number(row.avg_engagement).toFixed(1) : <>&mdash;</>}
        </Text>
      );
    case 'concern_flags':
      if (!row.concern_flags || row.concern_flags.length === 0) {
        return <Text size="sm" c="dimmed">—</Text>;
      }
      return (
        <Group gap={4} wrap="wrap">
          {row.concern_flags.map((f, i) => (
            <ConcernFlagBadge key={i} flag={f} />
          ))}
        </Group>
      );
    case 'date_submitted':
      if (!row.date_submitted) return <Text size="xs" c="dimmed">—</Text>;
      return <Text size="sm">{formatDate(row.date_submitted)}</Text>;
    default: {
      // Dynamic question columns
      if (columnId.startsWith('q_')) {
        const questionId = columnId.slice(2);
        const value = row.responses[questionId];
        const question = questions.find(q => q.question_id === questionId);
        if (!value) return <Text size="xs" c="dimmed">—</Text>;
        return <SurveyResponsePill value={value} questionType={question?.question_type || 'text'} />;
      }
      return <Text size="sm">—</Text>;
    }
  }
}
